import { Badge, Button, colors, Stack, Tooltip, Typography } from '@ui-components/neo.tax';
import { useMemo } from 'react';
import { CellProps, Column } from 'react-table';

import { ProviderLogo } from '../organisms/provider-logo';
import { DataSourcesTableItem, formatDateTime, getDataSourceTypeLabel } from '../utils';

type DataSourcesTableType =
  | 'connection'
  | 'file-upload'
  | 'payroll-file-upload'
  | 'ticketing-file-upload'
  | 'business-components-file-upload';

interface OptionalColumns {
  /** The kind of source, shown when one table mixes kinds. */
  type: boolean;
  /** Where the data came from; absent for CSVs the customer builds themselves. */
  provider: boolean;
  /** What a ticketing file holds. */
  entity: boolean;
}

/** Keyed on every table type, so adding one fails to compile until its columns are chosen. */
const OPTIONAL_COLUMNS: Record<DataSourcesTableType, OptionalColumns> = {
  connection: { type: true, provider: false, entity: false },
  'file-upload': { type: false, provider: true, entity: false },
  'payroll-file-upload': { type: false, provider: false, entity: false },
  'ticketing-file-upload': { type: false, provider: true, entity: true },
  'business-components-file-upload': { type: false, provider: false, entity: false },
};

const TYPE_COLUMN: Column<DataSourcesTableItem> = {
  accessor: 'itemType' as const,
  width: 240,
  Header: 'Type',
  Cell: ({
    value,
  }: React.PropsWithChildren<CellProps<DataSourcesTableItem, DataSourcesTableItem['itemType']>>): JSX.Element => (
    <Typography fontSize="xs" color={colors.gray[900]}>
      {getDataSourceTypeLabel(value)}
    </Typography>
  ),
};

const PROVIDER_COLUMN: Column<DataSourcesTableItem> = {
  accessor: 'providerLabel' as const,
  width: 200,
  Header: 'Provider',
  Cell: ({
    value,
  }: React.PropsWithChildren<CellProps<DataSourcesTableItem, DataSourcesTableItem['providerLabel']>>): JSX.Element => (
    <Typography fontSize="xs" color={colors.gray[900]}>
      {value ?? '—'}
    </Typography>
  ),
};

const ENTITY_COLUMN: Column<DataSourcesTableItem> = {
  accessor: 'entity' as const,
  width: 160,
  Header: 'Entity',
  Cell: ({
    value,
  }: React.PropsWithChildren<CellProps<DataSourcesTableItem, DataSourcesTableItem['entity']>>): JSX.Element => (
    <Typography fontSize="xs" color={colors.gray[900]}>
      {value ?? '—'}
    </Typography>
  ),
};

// TODO: https://linear.app/neo-tax/issue/NEO-6616/add-support-for-deleting-a-timesheet-on-the-data-source-page
const ITEM_TYPES_WITHOUT_ACTION: ReadonlySet<DataSourcesTableItem['itemType']> = new Set([
  'timesheet',
  'github-email-mapping',
  'accounting-token',
  'payroll-token',
]);

/** A row's Delete or Disconnect button, a disabled button saying why not, or nothing. */
const DataSourceRowAction = ({
  deletableItemTypes,
  item,
  setItemToDelete,
}: {
  item: DataSourcesTableItem;
  deletableItemTypes: ReadonlySet<DataSourcesTableItem['itemType']>;
  setItemToDelete: (item: DataSourcesTableItem) => void;
}): JSX.Element | null => {
  if (ITEM_TYPES_WITHOUT_ACTION.has(item.itemType)) {
    return null;
  }
  if (item.disabledActionText !== undefined) {
    return (
      <Button disabled size="xs" variant="action" rounded="regular">
        {item.disabledActionText}
      </Button>
    );
  }

  const label = deletableItemTypes.has(item.itemType) ? 'Delete' : 'Disconnect';
  return (
    <Button onClick={(): void => setItemToDelete(item)} size="xs" variant="action" rounded="regular">
      {label}
    </Button>
  );
};

/** A row's status pill. A connection that succeeded reads "Completed"; an upload reads "Validated". */
const DataSourceStatusBadge = ({
  item,
  tableType,
}: {
  item: DataSourcesTableItem;
  tableType: DataSourcesTableType;
}): JSX.Element => {
  switch (item.status) {
    case 'success': {
      const isConnection = tableType === 'connection';
      return (
        <Badge dot={false} colorVariant={isConnection ? 'blue' : 'green'}>
          {isConnection ? 'Completed' : 'Validated'}
        </Badge>
      );
    }
    case 'failure':
      return (
        <Tooltip text={item.statusDescription}>
          <Badge dot={false} colorVariant="red">
            Failed
          </Badge>
        </Tooltip>
      );
    case 'pending':
      return (
        <Badge dot={false} colorVariant="default">
          Processing
        </Badge>
      );
    case 'validating':
      return (
        <Badge dot={false} colorVariant="default">
          Validating
        </Badge>
      );
    case 'saved':
      return (
        <Badge dot={false} colorVariant="blue">
          Saved
        </Badge>
      );
    default:
      return (
        <Typography fontSize="xs" fontWeight="medium" color={colors.gray[600]}>
          ---
        </Typography>
      );
  }
};

/**
 * The Data Sources tables on both products, which differ in one thing only: which rows are a
 * "Delete" rather than a "Disconnect". A connection is disconnected and an upload is deleted, and
 * which item types are uploads is the product's to say — R&D has tax documents and ticketing files,
 * ASC has costs and payroll-month expenses.
 */
export const useDataSourcesTableColumns = ({
  canModify = true,
  deletableItemTypes,
  setItemToDelete,
  tableType = 'connection',
}: {
  /**
   * Whether to render the row's Delete/Disconnect action. False for viewers who cannot mutate the
   * source — the server rejects the mutation for them, so the button would only ever error.
   */
  canModify?: boolean;
  /** Item types whose action reads "Delete"; everything else reads "Disconnect". */
  deletableItemTypes: ReadonlySet<DataSourcesTableItem['itemType']>;
  setItemToDelete: (item: DataSourcesTableItem) => void;
  tableType?: DataSourcesTableType;
}): Column<DataSourcesTableItem>[] =>
  useMemo<Column<DataSourcesTableItem>[]>(
    () => [
      {
        id: 'displayName',
        accessor: (datum: DataSourcesTableItem): DataSourcesTableItem => datum,
        width: 380,
        Header: 'Name',
        Cell: ({
          value,
        }: React.PropsWithChildren<CellProps<DataSourcesTableItem, DataSourcesTableItem>>): JSX.Element => (
          <Stack direction="row" alignItems="center" gap={2} width={320} overflow="hidden">
            {value.provider ? <ProviderLogo provider={value.provider} size={24} /> : null}
            <Typography textOverflow="truncate" as="span" fontSize="xs">
              {value.title}
            </Typography>
          </Stack>
        ),
      },
      ...(OPTIONAL_COLUMNS[tableType].type ? [TYPE_COLUMN] : []),
      ...(OPTIONAL_COLUMNS[tableType].provider ? [PROVIDER_COLUMN] : []),
      ...(OPTIONAL_COLUMNS[tableType].entity ? [ENTITY_COLUMN] : []),
      {
        id: 'status',
        width: 140,
        Header: 'Status',
        accessor: (datum: DataSourcesTableItem): DataSourcesTableItem => datum,
        Cell: ({
          value,
        }: React.PropsWithChildren<CellProps<DataSourcesTableItem, DataSourcesTableItem>>): JSX.Element => (
          <DataSourceStatusBadge item={value} tableType={tableType} />
        ),
      },
      {
        accessor: 'originalSyncDate',
        width: 'auto',
        Header: tableType === 'connection' ? 'Connected On' : 'Uploaded on',
        Cell: ({
          value,
        }: React.PropsWithChildren<
          CellProps<DataSourcesTableItem, DataSourcesTableItem['originalSyncDate']>
        >): JSX.Element => (
          <Typography fontSize="xs" color={colors.gray[900]}>
            {formatDateTime(value)}
          </Typography>
        ),
      },
      ...(canModify
        ? [
            {
              id: 'delete',
              width: 140,
              accessor: (datum: DataSourcesTableItem): DataSourcesTableItem => datum,
              Cell: ({
                value,
              }: React.PropsWithChildren<CellProps<DataSourcesTableItem, DataSourcesTableItem>>): JSX.Element => (
                <Stack justifyContent="end" fullWidth>
                  <DataSourceRowAction
                    item={value}
                    deletableItemTypes={deletableItemTypes}
                    setItemToDelete={setItemToDelete}
                  />
                </Stack>
              ),
            } as Column<DataSourcesTableItem>,
          ]
        : []),
    ],
    [canModify, deletableItemTypes, setItemToDelete, tableType],
  );
